import clsx from 'clsx';
import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { ExternalLink } from '@doger/ui/components/ui/link';
import type { AIEventAgentResponse } from '@doger/core/shared/api';

import styles from './agent-message.module.scss';

type AgentMessageProps = {
  message: AIEventAgentResponse;
  className?: string;
  classes?: {
    root?: string;
    container?: string;
    content?: string;
    text?: string;
  };

  isLast: boolean;
};

export const AgentMessage = memo(
  ({ message, className, classes, isLast }: AgentMessageProps) => (
    <div className={clsx(styles.message, className, classes?.root)}>
      <div className={clsx(styles.message__container, classes?.container)}>
        <div
          className={clsx(styles.message__content, classes?.content, {
            [styles.last]: isLast,
          })}
        >
          <div className={clsx(styles.message__text, classes?.text)}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ExternalLink,
                table: (tableProps) => (
                  <div className={styles.tableContainer}>
                    <table {...tableProps} />
                  </div>
                ),
              }}
            >
              {preprocessStreamingMarkdown(
                message.text
                  // Some responses contained <br> "tags", which are invalid
                  .replaceAll('<br>', '\n')
              )}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  )
);

/**
 * Preprocesses markdown text to handle partial/incomplete markdown syntax gracefully
 * during streaming. This prevents raw markdown syntax from being displayed to users.
 */
function preprocessStreamingMarkdown(text: string): string {
  // Handle incomplete links [text](url - remove incomplete link syntax
  // This regex matches [text](url where url is incomplete (no closing parenthesis)
  // Only remove if it's at the end of the text (indicating it's still being streamed)
  return text.replace(/\[([^\]]*)\]\(([^)]*)$/g, (match, text, _url) => {
    // If there's no text in brackets, just return empty
    if (!text.trim()) {
      return '';
    }

    // If there's text but incomplete URL, show just the text
    return text;
  });
}
